import { useEffect, useRef } from 'react'
import { auth } from '../config/firebase.config'
import {
  subscribeToUserTopic,
  subscribeToStaffCheckInAlerts,
  unsubscribeFromStaffCheckInAlerts,
  subscribeToStaffTerminalAlerts,
  unsubscribeFromStaffTerminalAlerts,
  getFCMToken,
  requestNotificationPermission,
} from '@hapana/shared/web/fcm.service'
import useSiteStore from '_stores/site.store'

export const useFCMNotifications = () => {
  const currentSite = useSiteStore((state) => state.currentSite)
  const fcmTokenRef = useRef(null)
  const previousSiteIdRef = useRef(null)
  const fcmChannelRef = useRef(null)
  const unsubscribeOnMessageRef = useRef(null)

  useEffect(() => {
    // Subscribe to FCM notifications when user is authenticated
    const setupFCMSubscription = async () => {
      try {
        if (!import.meta.env.VITE_FCM_VAPID_KEY) {
          console.warn('VITE_FCM_VAPID_KEY not set, skipping FCM setup')
          return
        }

        const currentUser = auth.currentUser
        if (!currentUser) {
          return
        }
        const hasPermission = await requestNotificationPermission()
        if (!hasPermission) {
          console.warn('Notification permission not granted')
          return
        }
        const { getMessaging, getToken, onMessage } = await import('firebase/messaging')
        const messaging = getMessaging(auth.app)
        const fcmToken = await getFCMToken(auth.app, import.meta.env.VITE_FCM_VAPID_KEY, {
          getMessaging,
          getToken,
        })

        // Handle foreground FCM messages — forward to BroadcastChannel
        // (background messages are handled by firebase-messaging-sw.js)
        const fcmChannel = new BroadcastChannel('fcm-notifications')
        fcmChannelRef.current = fcmChannel
        const unsubscribeOnMessage = onMessage(messaging, (payload) => {
          fcmChannel.postMessage({
            type: 'FCM_NOTIFICATION',
            data: payload,
          })
        })
        unsubscribeOnMessageRef.current = unsubscribeOnMessage
        if (fcmToken) {
          fcmTokenRef.current = fcmToken
          const subscribed = await subscribeToUserTopic(fcmToken)
          if (!subscribed) {
            console.error('Failed to subscribe to user topic')
          }

          const siteId = currentSite?.siteId
          if (siteId) {
            await subscribeToStaffCheckInAlerts(fcmToken, siteId)
            await subscribeToStaffTerminalAlerts(fcmToken, siteId)
            previousSiteIdRef.current = siteId
          }
        } else {
          console.warn('No FCM token available')
        }
      } catch (error) {
        console.error('Failed to setup FCM subscription:', error)
      }
    }

    // Set up subscription on mount if user is already authenticated
    setupFCMSubscription()

    // Listen for auth state changes for FCM setup
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setupFCMSubscription()
      }
    })

    return () => {
      unsubscribe()
      if (unsubscribeOnMessageRef.current) {
        unsubscribeOnMessageRef.current()
      }
      if (fcmChannelRef.current) {
        fcmChannelRef.current.close()
      }
    }
  }, [])

  // Re-subscribe to staff check-in/terminal alerts when site changes
  useEffect(() => {
    const fcmToken = fcmTokenRef.current
    if (!fcmToken) {
      return
    }

    const newSiteId = currentSite?.siteId ?? null
    const prevSiteId = previousSiteIdRef.current

    if (newSiteId === prevSiteId) {
      return
    }

    const handleSiteChange = async () => {
      // Subscribe to the new site first so the user always receives alerts for the
      // location they just switched to, even if cleaning up the previous topic fails.
      if (newSiteId) {
        await subscribeToStaffCheckInAlerts(fcmToken, newSiteId)
        await subscribeToStaffTerminalAlerts(fcmToken, newSiteId)
      }
      if (prevSiteId) {
        await unsubscribeFromStaffCheckInAlerts(fcmToken, prevSiteId)
        await unsubscribeFromStaffTerminalAlerts(fcmToken, prevSiteId)
      }
      previousSiteIdRef.current = newSiteId
    }

    // Fire-and-forget: a topic subscription failure is non-fatal. Subscribing to the
    // new site happens first, so the user still receives alerts for their current
    // location even if updating subscriptions partially fails.
    handleSiteChange().catch(() => {})
  }, [currentSite?.siteId])
}
